import {BaseElement, BASIS, OSkinableInit} from "back/commons/basis";
import {IPopupable} from "back/commons/widgets/popupable";
import {POPUP} from "back/commons/widgets/popups";
import {ItemXmlEd} from "back/wsp/views/item/itemXmlEd";
import {Desk, UserActiveDeskFeat} from "lib/commons/desk";
import {IReg, IUiEnv, REG} from 'lib/commons/registry';
import {CDM} from "lib/commons/utils/cdm";
import {DOM, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {IXAddr} from "lib/commons/xml/xAddr";
import {IChainEnv} from "lib/wsp/chain";
import {DYNGEN, JOrigin} from "lib/wsp/dyngen";
import {JSrcIdent, SRC, srcRef} from "lib/wsp/src";
import {IWspEnv, IWspUiEnv, JWspUriChangeMsg, JWspWorkingState, WSP, Wsp, wspCd, wspRef} from "lib/wsp/wsp";
import {IDatasEditor} from "lib/wsp/wspMetaUi";
import {IView} from "lib/commons/views";
import "back/wsp/views/views_Perms";
import {EventMgr, EventsMgrProxy} from "lib/commons/events";
import {JDynGenDatas} from "back/wsp/plugins/dynGenPlg";
import {IEndPoint, IUrlCheckRequest} from "lib/commons/io/io";
import {Resizer} from "back/commons/widgets/resizer";
import {FontSizeDeskFeat, UiThemeDeskFeat} from "back/core/plugins/optionsPlg";
import {captureScenariProtocol} from "lib/core/scenariProtocol";
import {EWspChangesEvts, EWspWorkingSt, IWspLivePlaceEvents, IWspsLivePlacePointer, WspsLivePlace} from "lib/wsp/wspsLive";
import {ERROR} from "lib/core/errorReport";
import {REMOTE} from "lib/core/remote";

/**
 * API à implémenter par les pages fournies par le dynGen.
 */
export interface IWindowDynGen extends Window {

	/** Appelé sur le onLoad de la page web du dynGen. */
	initParentFrame?(parentFrame: IDynGenParentFrame): void

	/** Appelé lors d'une détection d'un changement d'un item impliqué dans la construction de cette page. */
	reloadPage?(srcRefs?: srcRef[]): void

	/** Listeners appelés par reloadPage(). */
	beforeReloadPageLstn?: Array<(srcRef: srcRef | null) => void | 'stop'>
	afterReloadPageLstn?: Array<(srcRef: srcRef | null, updates?: { container: Element, oldNodes: Node[] }[]) => void>

	/** Config des éditeurs qui sera analysé par DynGenApp. */
	editorConfigs?: OEditorConfig[]

	/**
	 * Si editorConfigs est spécifié, DynGenApp peut appeler enableEditors() pour chacune de ces config
	 * (après avoir controlé les permissions).
	 * Cette méthode doit identifier toutes les racines éditables et les faire adhérer à IEditableNode.
	 */
	enableEditors?(conf: OEditorConfig): void

	/**
	 * Notifie le dynGen que cette origine est en cours d'édition par le DynGenApp.
	 */
	markAsEditing?(origin: JOrigin): void

	/**
	 * Notifie la page du générateur des items actuellement verrouilés en écriture :
	 * - au chargement de la page pour chaque item de la page si l'item est actuellement verrouilé (locked=true)
	 * - ou au changement d'état de lock d'un des items exploités dans la page.
	 *
	 * Cette méthode est obligatoire si IDynGenParentFrame.lockItem() est utilisé par le générateur (commentaires...).
	 * Si une édition a démarré sur l'item srcRef mais que la page reçoit un évènement itemLocked(srcRef, true), alors la page
	 * DOIT abandonner l'édition.
	 */
	itemLocked?(srcRef: srcRef, locked: boolean): void

	/**
	 * Notifie qu'un lock préalablement posé via sc-lockItem a été perdu suite à un timeout.
	 * XXX utilité ?
	 */

	//lockLoosed?(srcRef: srcRef): void

	/**
	 * Notifie d'une perte - ou d'une récupération - de la connexion.
	 */
	onConnection(ok: boolean): void

	rootEditableContents? : HTMLElement
}

/**
 * Contexte parent des pages du dynGen, fournit à la page du dynGen
 * via IWindowDynGen.initParentFrame(parentFrame: IDynGenParentFrame)
 */
interface IDynGenParentFrame {

	/** Controle du droit en écriture */
	canEdit(srcRef: srcRef): Promise<boolean>

	/**
	 * Permet de démarrer une édition d'un fragment d'un item.
	 * @param webNode Noeud de la page du dynGen. Ce noeud doit avoir dans son contexte
	 *    ancêtre la définition d'une source à éditer via les atrributs data-origin.
	 */
	editFragment(webNode: IEditableNode, oripathHandler: IEntryOriginHandler): void

	/**
	 * Demande de poser un lock en édition sur un item (Pour l'édition directe des commentaires notamment).
	 * - Gestion "optimiste" des race-conditions : une fois l'event dispatché, la page peut considérer le verrou acquis,
	 *   mais il est théoriquement possible qu'une notification contraire intervienne dans les millisecondes qui
	 *   suivent exigeant l'abandon de l'édition via IWindowDynGen.itemLocked(srcRef, true);
	 * - Si l'utilisatur n'est plus actif, ou si la connection est instable, le verrou peut être perdu
	 *   et une notification IWindowDynGen.itemLocked(srcRef, true) peut toujours intervenir à tout moment.
	 * - Malgré le verrou posé, un reloadPage() peut toujours avoir lieu pour diverses raisons (perte de verrou,
	 *   suppression ou import massif, mise à jour du modèle documentaire, changement de droits...).
	 *
	 * @return false si un lock est déjà en cours.
	 */
	lockItem(srcRef: srcRef): boolean

	/**
	 * Elimine le verrou précédemment posé via IDynGenParentFrame.lockItem(srcRef)
	 */
	unlockItem(srcRef: srcRef): void

	/** Injecte le thème courant dans la page du dynGen. */
	injectTheme(): void

	/** Evalue une permission donnée sur le repo courant */
	hasPerm(perm: string): Promise<boolean>

	/** Evalue une permission donnée sur un item de l'atelier courant */
	hasPermOnSrcRef(perm: string, srcRef?: string): Promise<boolean>
}

/**
 * CustomElement d'affichage d'un dynGen.
 * Attributs :
 * - Init config atts : auto-resize="true"|"false"
 */
export interface DynGen extends BaseElement, IDynGenParentFrame {
	initialize(init: ODynGenInit): this;
}

export interface ODynGenInit extends OSkinableInit, JDynGenDatas {
	reg?: IReg<IChainEnv & IUiEnv>

	autoResize?: boolean
}

export class DynGen extends BaseElement implements IDynGenParentFrame, IView {

	configs: ODynGenInit;

	reg: IReg<IChainEnv & IUiEnv>;

	/** SubReg de this.reg enrichi du wsp. */
	wspReg: IReg<IWspUiEnv>;

	place: WspsLivePlace;

	wsp: Wsp;

	genFrame: HTMLIFrameElement;

	/**
	 * empty : dynGen vide, correspond à un 404, gen non trouvé pour ce type d'item.
	 * message : stackTrace publiée dans la page. (TODO)
	 */
	get onError(): EventMgr<(err: 'empty' | 'message', msg: Element, dynGen: DynGen) => void> {return this._onError || (this._onError = new EventMgr())}

	protected _onError: EventMgr<(err: 'empty' | 'message', msg: Element, dynGen: DynGen) => void>;

	/**
	 * Srcs impliqués dans la page courante du dynGen.
	 * En value : Date du srcRef issu du data-origin trouvé dans la page = field srcTreeDt de l'item.
	 */
	srcRefs: Map<srcRef, number> = new Map();

	/** Verrou en cours des autres devices. */
	wspRefsLockedBy: Map<wspRef, string/*clId du device détenant le lock*/> = new Map();

	/** Panel d'édition. */
	resizer: Resizer;
	panel: HTMLElement;
	modalPopup: IPopupable;

	/** Locks actuellement détenus par le générateur (édition comments). */
	srcRefsLockedByGen: Set<srcRef> = new Set();
	locksByGenDisabled: boolean;

	/** Origin en cours d'édition. */
	currentOrigin: JOrigin;

	/** Editeur en cours. */
	currentEditor: ItemXmlEd;

	/** Mémoire des listeners pour remove. */
	protected onInactiveUser: (since: number) => void;
	protected onActiveUser: () => void;

	protected _initialize(init: ODynGenInit) {
		this.reg = this.findReg(init);
		this.configs = init;

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);

		this.genFrame = sr.appendChild(<iframe/>) as HTMLIFrameElement;
		this.genFrame.setAttribute("sandbox", "allow-downloads allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts");
		this.genFrame.addEventListener("load", this.onLoadedPage);
	}

	buildInitFromAtts(init?: ODynGenInit): any {
		init = super.buildInitFromAtts(init);
		if (this.hasAttribute("auto-resize")) init.autoResize = BASIS.extractAttr(this, "auto-resize") !== "false";
		return init;
	}

	async onViewShown() {
		if (this.place) return;
		//Pas encore init.
		const universe = this.reg.env.universe;
		const wspCd = this.configs.w;
		const wspOrigin = (this.reg.env as IWspEnv).wsp;
		if (!wspOrigin || wspOrigin.code !== wspCd) {
			this.place = universe.wspServer.wspsLive.newPlace();
			this.wsp = this.place.getWsp(this.configs.w); //écoute les events pour ce wsp.
			if (!this.wsp.isAvailable) await this.wsp.waitForAvailable(this);
			this.wspReg = REG.createSubRegMixed(this.reg, this.wsp.reg); //on doit attendre le chargement du wsp pour que this.wsp.reg soit complet (roles...)
			this.wspReg.env.uiRoot = this;
			this.wspReg.env.place = this.place;
		} else {
			this.wsp = wspOrigin;
			this.wspReg = this.reg;
			this.place = (this.reg.env as IWspsLivePlacePointer).place;
		}

		if (!this.place) this.place = universe.wspServer.wspsLive.newPlace();
		else this._eventsMgr = new EventsMgrProxy(this.place.eventsMgr);

		//this.wspReg.env.infoBroker = ... //pas utile pour le moment.
		(this._eventsMgr || this.place.eventsMgr)
			.on("wspUriChange", (msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') => {
				if (!this.isConnected) return;
				if (msg.type === EWspChangesEvts.u && msg.wspCd === this.configs.w) {
					const srcRef = SRC.srcRef(msg);
					if (DEBUG_DYNGEN) console.log("DYNGEN - wspUriChange:::::", srcRef, this.srcRefs.has(srcRef));
					if (this.srcRefs.has(srcRef)) {
						const win = this.genFrame.contentWindow as IWindowDynGen;
						if (win.reloadPage) win.reloadPage([srcRef]);
						else win.location.reload();
						if (from === 'server') this.closeEditor();
					}
				}
			})
			.on('newWspWorkingState', (wspRef: wspRef, wspCd: wspCd, srcRef: srcRef, st: EWspWorkingSt, account: string, clId: string) => {
				if (!this.isConnected) return;
				const win = this.genFrame.contentWindow as IWindowDynGen;
				if (!win.itemLocked) return;
				if (st === EWspWorkingSt.w) {
					this.wspRefsLockedBy.set(wspRef, clId);
					win.itemLocked(srcRef, true);
				} else if (this.wspRefsLockedBy.get(wspRef) === clId) {
					this.wspRefsLockedBy.delete(wspRef);
					win.itemLocked(srcRef, false);
				}
			})
			.on('houseDirtyChange', (wspRef: wspRef, wsp: Wsp, srcIdent: JSrcIdent, dirty: boolean) => {
				if (!this.isConnected) return;
				const win = this.genFrame.contentWindow as IWindowDynGen;
				if (!win.itemLocked) return;
				const srcRef = srcIdent.srcId || srcIdent.srcUri;
				if (this.srcRefs.has(srcRef)) win.itemLocked(srcRef, dirty);
			})
			.on("onConnectionLost", () => {
				if (!this.isConnected) return;
				const win = this.genFrame.contentWindow as IWindowDynGen;
				if (DEBUG_DYNGEN) console.log("DYNGEN - onConnectionLost::::");
				if (win && win.onConnection) win.onConnection(false);
			})
			.on("onConnectionRenewed", async () => {
				if (!this.isConnected) return;
				const win = this.genFrame.contentWindow as IWindowDynGen;
				if (this.srcRefs.size > 0) {
					//Une page est initialisée
					const srcRefs = Array.from(this.srcRefs.keys());
					const ts = await WSP.fetchSrcs(this.wsp, this, srcRefs, ["srcTreeDt"]);
					let reload: srcRef[];
					for (let i = 0; i < srcRefs.length; i++) {
						if (this.srcRefs.get(srcRefs[i]) !== ts[i].srcTreeDt) {
							if (!reload) reload = [];
							reload.push(srcRefs[i]);
						}
					}
					if (reload) {
						if (DEBUG_DYNGEN) console.log("DYNGEN - onConnectionRenewed::::reloadPage", reload);
						if (win.reloadPage) win.reloadPage(reload);
						else win.location.reload();
						return;
					}
					if (DEBUG_DYNGEN) console.log("DYNGEN - onConnectionRenewed::::ok");
					if (win && win.onConnection) win.onConnection(true);
					this.initLocks(win);
				}
			});

		UserActiveDeskFeat.add(desk);
		this.onInactiveUser = this._onInactiveUser.bind(this);
		(desk as UserActiveDeskFeat).userActiveLstn.on("inactive", this.onInactiveUser);
		this.onActiveUser = this._onActiveUser.bind(this);
		(desk as UserActiveDeskFeat).userActiveLstn.on("active", this.onActiveUser);

		this.reload();
	}

	/** rechargement complet de la frame */
	reload(): void {
		this.genFrame.src = this.getDynGenUrl(this.configs).url;
	}

	/** (re)chargement complet de la frame */
	load(): void {
		if (this.place)
			this.reload()
		else
			this.onViewShown();
	}


	getDynGenUrl(data: JDynGenDatas): IEndPoint {
		const params = Object.create(data.gp || null);
		params.refUri = data.r;
		const path = encodeURIComponent(data.w) + "/" + data.c + "/" + DYNGEN.encodeUrl2(CDM.stringify(params)) + "/" + (data.pp == null ? "co/" : data.pp) + (data.qs ? "?" + data.qs : "");
		return this.reg.env.universe.wspServer.config.itemDynGenUrl.resolve(path);
	}

	/** Proxy vers l'eventsMgr de la place existante. */
	protected _eventsMgr: EventsMgrProxy<IWspLivePlaceEvents>;

	onViewHidden(closed?: boolean) {
		this.closeEditor();
		if (closed) {
			this.wsp = null;
			if (this.wspReg && this.wspReg !== this.reg) {
				this.wspReg.close();
				this.wspReg = null;
			}
			if (this.place) {
				if (this._eventsMgr) this._eventsMgr.removeAllListeners(); //place parente utilisée
				else this.place.closePlace(); //place dédiée
				this.place = null;
				this._eventsMgr = null;
			}
			if (this.onInactiveUser) {
				(desk as UserActiveDeskFeat).userActiveLstn.removeListener("inactive", this.onInactiveUser);
				this.onInactiveUser = null;
			}
			if (this.onActiveUser) {
				(desk as UserActiveDeskFeat).userActiveLstn.removeListener("active", this.onActiveUser);
				this.onActiveUser = null;
			}
		}
	}

	async canEdit(srcRef: srcRef): Promise<boolean> {
		if (!srcRef) return false;
		const h = this.place.wspsLive.getHouseIfFetched(WSP.buildWspRef(this.wsp.code, srcRef));
		if (h) return this.wsp.reg.hasPermission(PERM_EDIT, h.srcFields.srcRoles, null, h.srcFields.srcRi);
		//Besoin de charger l'item
		try {
			const sd = await this.wsp.fetchShortDesc(srcRef, this);
			return this.wsp.reg.hasPermission(PERM_EDIT, sd.srcRoles, null, sd.srcRi);
		} catch (e) {
			console.log(e);
			return false;
		}
	}

	/** Demande d'édition d'un noeud. */
	editFragment(editableNode: IEditableNode): void {
		if (!Desk.checkCompat('edit')) {
			POPUP.showNotifForbidden(<div>
				<p>Ce navigateur n'est pas compatible avec l'édition de texte riche.</p>
				<p>Vous pouvez utiliser un des navigateurs à jour suivants :</p>
				<ul>
					{Desk.knownNavCompat.map((n) => n.url ? <li><a href={n.url} target="_blank">{n.name}</a></li> : <li>{n.name}</li>)}
				</ul>
			</div>, this);
			return;
		}
		let origin = DYNGEN.getOriginFrom(editableNode);
		const editorConf = editableNode.editorConfig;
		if (editorConf.editEntryPoint) origin = editorConf.editEntryPoint.handleOrigin(origin);
		if (!origin.srcRef) return;
		this.openEditor(origin, new ItemXmlEd().initialize({
			reg: this.wspReg,
			srcRef: origin.srcRef,
			writePerms: PERM_EDIT,
			wedMgrConfig: {
				xaRoot: xaRootFromOrigin(origin)
			},
			editorKey: editorConf.editorKey,
			datasEditor: editorConf.editor,
			saveOnClose: true,
			initItemReg: (reg) => {
				//Diff avec un autre item lors de l'édition d'un framgent dans le contexte d'un dynGen est douteux/compliqué... on masque.
				reg.addToList("wed.diffBar.actions", "otherItem", 2, null);
			}
		}), editorConf.editMode || 'modal');
	}

	lockItem(srcRef: srcRef): boolean {
		if (this.place.lockExternal(WSP.buildWspRef(this.wsp.code, srcRef))) {
			this.srcRefsLockedByGen.add(srcRef);
			this.closeEditor();
			return true;
		}
		return false;
	}

	unlockItem(srcRef: srcRef) {
		if (this.srcRefsLockedByGen.has(srcRef)) {
			this.srcRefsLockedByGen.delete(srcRef);
			this.place.unlockExternal(WSP.buildWspRef(this.wsp.code, srcRef));
		}
	}

	injectTheme() {
		if (UiThemeDeskFeat.isIn(desk)) desk.injectThemeInSubFrame(this.genFrame);
		if (FontSizeDeskFeat.isIn(desk)) desk.injectFontSizeInSubFrame(this.genFrame);
	}

	async hasPerm(perm: string): Promise<boolean> {
		const libPath = await REG.findScPermLibPath(perm);
		if (this.reg.env.resolver && libPath)
			await import(this.reg.env.resolver.resolvePath(libPath));
		return this.reg.hasPerm(perm);
	}

	async hasPermOnSrcRef(perm: string, srcRef?: string): Promise<boolean> {
		if (!this.wsp) return null;
		const libPath = await REG.findScPermLibPath(perm);
		if (this.reg.env.resolver && libPath)
			await import(this.reg.env.resolver.resolvePath(libPath));
		if (srcRef) {
			const sd = await this.wsp.fetchShortDesc(srcRef, this);
			return this.wsp.reg.hasPermission(perm, sd.srcRoles, null, sd.srcRi);
		} else {
			return this.wsp.reg.hasPerm(perm);
		}
	}

	protected _randomId: string;

	/** Chargement d'une nouvelle page du dynGen. */
	protected async onLoadedPage(this: HTMLIFrameElement) {
		const dynGen = DOMSH.findHost(this) as DynGen;
		const win = this.contentWindow as IWindowDynGen;
		let winLoc;
		try {
			// Une exception est lancé si bloqué par la sécurité
			winLoc = win.location.href;
		} catch (e) {}
		if (!winLoc || winLoc === "about:blank") {
			//chargement intial de l'iframe ou reset de la page.
			dynGen.onUnloadPage();
			return;
		}

		if (dynGen._onError) {
			const body = win.document.body;
			if (!body || !body.hasChildNodes()) {
				dynGen._onError.emit('empty', null, dynGen);
				dynGen.onUnloadPage();
				return; //on sort
			} else {
				//TODO eval si message / stackTrace...
				//dynGen._onError.emit('message', body...., dynGen);
				//return; //on sort
			}
		}

		if (dynGen.configs.autoResize) new ResizeObserver(dynGen.onBodyResize.bind(dynGen)).observe(win.document.documentElement);

		//déclaration du contexte parent.
		//try {
		if (win.initParentFrame) win.initParentFrame(dynGen);

		await dynGen._initAfterLoad(win);

		//On s'abonne aux mises à jour des reloadPages()
		if (win.afterReloadPageLstn) win.afterReloadPageLstn.push((srcRef: srcRef | null, updates?: { container: Element, oldNodes: Node[] }[]) => {
			dynGen._initAfterLoad(win);
		});
		// } catch (e) {
		// 	POPUP.showNotif("Impossible d'afficher cette vue", this);
		// 	try {
		// 		win.document.documentElement.textContent = null; //On efface la page.
		// 	} catch (e) {console.log(e)}
		// 	throw e;
		// }
		captureScenariProtocol(this, dynGen.wspReg);
	}

	onBodyResize(entries: ResizeObserverEntry[]) {
		this.genFrame.style.height = entries[entries.length - 1].contentRect.height + 'px';
	}

	async _initAfterLoad(win: IWindowDynGen) {
		if (!win.document.body) {
			//document killé entre temps.
			this.onUnloadPage();
			return;
		}

		// Initialisation de la vérification des URLs
		this.initUrlCheck(win);

		//Mise à jour de la map des srcRefs de cette page.
		const oldSrcRefs = this.srcRefs;
		this.srcRefs = new Map();
		DYNGEN.findSrcRefs(win.rootEditableContents || win.document.body, (elt, srcRef, lastModif) => {
			this.srcRefs.set(srcRef, lastModif);
		});
		if (DEBUG_DYNGEN) console.log("DYNGEN - new map srcRefs::::", Array.from(this.srcRefs));

		//Config des éditeurs
		const editorConfigs = win.editorConfigs;
		if (editorConfigs) {
			for (const conf of editorConfigs) {
				//FIXME check Perms
				win.enableEditors(conf);
			}
			if (this.currentOrigin && win.markAsEditing) win.markAsEditing(this.currentOrigin);
		}

		await this.initLocks(win);
		if (!this.wsp) return; //racecond

		//Mise à jour des srcRefs dont écouter les locks.
		const wspCd = this.wsp.code;
		for (const newSrcRef of this.srcRefs.keys()) {
			if (oldSrcRefs.has(newSrcRef)) {
				oldSrcRefs.delete(newSrcRef);
			} else {
				this.place.setState(WSP.buildWspRef(wspCd, newSrcRef), EWspWorkingSt.l); //TODO 'l' ou 'r' configurable ?
			}
		}
		for (const srcRefToRem of oldSrcRefs.keys()) {
			this.place.setState(WSP.buildWspRef(wspCd, srcRefToRem), EWspWorkingSt.none);
		}
	}

	protected onUnloadPage() {
		if (this.srcRefs.size > 0) {
			const wspCd = this.wsp.code;
			for (const srcRef of this.srcRefsLockedByGen) {
				this.place.unlockExternal(WSP.buildWspRef(wspCd, srcRef));
			}
			this.srcRefsLockedByGen.clear();
			for (const srcRef of this.srcRefs.keys()) {
				this.place.setState(WSP.buildWspRef(wspCd, srcRef), EWspWorkingSt.none);
			}
			this.srcRefs.clear();
		}
	}

	protected async initLocks(win: IWindowDynGen) {
		this.wspRefsLockedBy.clear();
		if (win.itemLocked) {
			//Le gen implémente l'écoute des items lockés, on l'initialise.
			try {
				for (const srcRef of this.srcRefs.keys()) {
					const wspRef = WSP.buildWspRef(this.wsp.code, srcRef);
					let wspSt: JWspWorkingState;
					if (this.place.isHouseDirty(wspRef) || (wspSt = (await this.place.getStates(wspRef)).find((st) => st.state === EWspWorkingSt.w))) {
						//dynGen.wspRefsLockedBy.set(wspRef, wspSt ? wspSt.clId : '.'/* '.' = notre house est dirty. */);
						if (wspSt) this.wspRefsLockedBy.set(wspRef, wspSt.clId);
						if (DEBUG_DYNGEN) console.log("DYNGEN - lock::::", srcRef);
						win.itemLocked(srcRef, true);
					}
				}
			} catch (e) {
				if (win.onConnection) win.onConnection(false);
			}
		}
	}

	protected async openEditor(origin: JOrigin, editor: ItemXmlEd, editMode: 'modal' | 'panel'): Promise<void> {
		await this._killEditor();
		try {
			await editor.initializedAsync;
			this.currentEditor = editor;
			this.currentOrigin = origin;
		} catch (e) {
			POPUP.showNotifError("Impossible d'éditer ce fragment.", this);
			throw e;
		}
		if (editMode === 'panel') {
			if (!this.panel) {
				const sr = this.shadowRoot;
				this.genFrame.setAttribute("c-resizable", "");
				this.resizer = sr.appendChild(<Resizer c-orient="row"/>) as Resizer;
				this.panel = sr.appendChild(<div class="panel" c-resizable=""/>);
			}
			DOM.setHidden(this.panel, false);
			DOM.setHidden(this.resizer, false);
			this.panel.appendChild(editor);
			const markAsEditing = (this.genFrame.contentWindow as IWindowDynGen).markAsEditing;
			if (markAsEditing) markAsEditing(origin);
		} else {
			const st = editor.style;
			st.display = 'flex';
			st.minHeight = '0';
			st.minWidth = '0';
			st.flex = '1';
			this.modalPopup = POPUP.showDialog(editor, this, {
				titleBar: {
					barLabel: {label: "Édition du bloc"},
					closeButton: {uiContext: 'dialog', label: "Fermer", title: "Fermer l'édition du bloc"}
				},
				resizer: {},
				initWidth: '80vw',
				initHeight: '60vh'
			});
			this.modalPopup.onNextClose().then(() => {
				this.modalPopup = null;
				this.closeEditor();
			})
		}
	}

	onViewBeforeHide(closed?: boolean): boolean {
		return !closed || !this.currentEditor || !this.currentEditor.isDirty();
	}

	async onViewWaitForHide(closed?: boolean): Promise<boolean> {
		if (!closed) return true;
		try {
			await this.closeEditor();
			return true;
		} catch (e) {
			return false;
		}
	}

	async closeEditor(): Promise<void> {
		await this._killEditor();
		if (this.modalPopup) {
			this.modalPopup.close();
			this.modalPopup = null;
		} else if (this.panel) {
			DOM.setHidden(this.panel, true);
			DOM.setHidden(this.resizer, true);
		}
	}

	protected async _killEditor(): Promise<void> {
		if (this.currentEditor) {
			this.currentEditor.closeEditor();
			this.currentEditor.remove();
			this.currentEditor = null;
			this.currentOrigin = null;
		}
	}

	protected _onInactiveUser() {
		this.locksByGenDisabled = true;
		for (const srcRef of this.srcRefsLockedByGen) {
			this.place.unlockExternal(WSP.buildWspRef(this.wsp.code, srcRef));
		}
	}

	protected _onActiveUser() {
		this.locksByGenDisabled = false;
		for (const srcRef of this.srcRefsLockedByGen) {
			this.place.lockExternal(WSP.buildWspRef(this.wsp.code, srcRef));
		}
	}

	protected initUrlCheck(win: Window) {
		const htmlElem = win.document.documentElement;
		if (htmlElem && htmlElem.getAttribute("data-checkUrl-active") == "true") {
			const checkUrlItems = htmlElem.querySelectorAll("*[data-checkUrl]");
			if (checkUrlItems.length) {
				let param: ICheckUrlGlobalParam = {};
				const paramStr = htmlElem.getAttribute("data-checkUrl-param");
				if (paramStr) {
					try {
						param = JSON.parse(paramStr);
					} catch (e) {
						ERROR.log("Error parsing global checkUrl param", e);
					}
				}
				if (!param.maxConcurrentCheckUrl) param.maxConcurrentCheckUrl = 5;
				// Pas d'attente du retour, les items sont notifiés par event
				this.checkUrls(checkUrlItems, param);
			}
		}
	}

	async checkUrls(items: NodeListOf<Element>, globalParam: ICheckUrlGlobalParam) {
		const itemsByUrl: Dict<Element> = {};
		const requests: IUrlCheckRequest[] = [];

		// Construction de l'ensemble des requêtes
		for (const item of items) {
			const url = item.getAttribute("data-checkUrl");
			try {
				item.setAttribute("data-checkUrl-status", "pending");
				itemsByUrl[url] = item;
				let param: ICheckUrlParam = globalParam;
				const paramStr = item.getAttribute("data-checkUrl-param");
				if (paramStr) param = Object.assign(Object.create(globalParam), JSON.parse(paramStr));
				const request: IUrlCheckRequest = {url};
				if (typeof param.headerList === "string") request.extractHeaders = param.headerList.split(',');
				if (typeof param.method === "string") request.method = param.method.split(',');
				requests.push(request);
			} catch (e) {
				ERROR.log(`Error while preparing the checkUrl request: ${url}`, e);
			}
		}

		// Appels du contrôle d'URL
		while (requests.length) {
			const batch = requests.splice(0, globalParam.maxConcurrentCheckUrl);
			const reports = await REMOTE.checkUrls(batch, globalParam.maxConcurrentCheckUrl, this.reg);

			for (const url in reports) {
				const item = itemsByUrl[url];
				item.setAttribute("data-checkUrl-report", JSON.stringify(reports[url]));
				item.dispatchEvent(new CustomEvent("checkUrlResponse", {bubbles: true, cancelable: true}));
				item.setAttribute("data-checkUrl-status", "done");
			}
		}
	}
}

REG.reg.registerSkin('wsp-dyngen', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
	}

	iframe {
		flex: 1 1 15em;
		border: none;
		width: 0; /* sinon bloqué à 300px mini via un c-resizer */
		user-select: none;
	}

	.panel {
		flex: 0 0 20%;
		display: flex;
		min-height: 0;
		min-width: 0;
	}

	wsp-item-xmled {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
	}

	[hidden] {
		display: none;
	}
`);

customElements.define('wsp-dyngen', DynGen);

interface ICheckUrlParam {
	method?: string;
	headerList?: string;
}

interface ICheckUrlGlobalParam extends ICheckUrlParam {
	maxConcurrentCheckUrl?: number;
}

/** Config des éditeurs dans la page. */
type OEditorConfig = {

	/** Permissions restreignant les éditeurs accessibls à l'utilisateur, quelquesoit l'item. Donc évaluée a niveaude l'atelier. */
	perms?: string | string[]

	editMode?: 'modal' | 'panel'
	editorKey: string
	editEntryPoint?: IEntryOriginHandler
	/* Si editorKey non utilisé : config cutsom de l'editor, non déclarée dans l'itemType. */
	editor?: IDatasEditor

	//interne au dynGen.
	//rootsSelector: string //"div.text,.infoblock_ti>span"
}

/** function de modification de l'oripath avant édition. */
type IEntryOriginHandler = {
	handleOrigin(oripath: JOrigin): JOrigin
}

/** Noeud susceptible d'être édité dans une page du dynGen. */
interface IEditableNode extends Element {
	editorConfig: OEditorConfig
}


const PERM_EDIT = "action.dynGen#edit.node";


function xaRootFromOrigin(origin: JOrigin): IXAddr {
	const frags = origin.oriPath.split("/");
	const xa: IXAddr = [];
	for (let i = 0; i < frags.length; i++) xa.push(parseInt(frags[i], 10));
	return xa;
}

const DEBUG_DYNGEN = false;

// Impl exemple de reloadPage() dans IWindowDynGen
// // let tplNode:HTMLTemplateElement;
// async function reloadPage(srcRef?: srcRef): Promise<void> {
// 	for (let lstn of (window as IWindowDynGen).beforeReloadPageLstn) if (lstn(srcRef) === 'stop') return;
// 	let resp = await fetch(location.href, {redirect: 'manual'});
// 	if (resp.status === 301 /*resp.redirected*/) {
// 		//Redirect
//
// 	} else if (resp.ok) {
// 		//refresh
// 		let xmlText = await resp.text();
// 		// if(!tplNode) tplNode = document.head.appendChild(document.createElement("template")) as HTMLTemplateElement;
// 		// tplNode.content.innerHTML = xmlText;
// 		let dom = new DOMParser().parseFromString(xmlText, (document as any).contentType === "text/html" ? "text/html" : "text/xml"); //resp.headers.get("content-type") === "http://www.w3.org/1999/xhtml"
// 		let updates = [{
// 			container: document.body,
// 			oldNodes: Array.from(document.body.childNodes)
// 		}];
// 		document.title = dom.title;
// 		document.body.textContent = null;
// 		let newBody = dom.documentElement.querySelector('body');
// 		while (newBody.firstChild) document.body.appendChild(document.adoptNode(newBody.firstChild));
// 		for (let lstn of (window as IWindowDynGen).afterReloadPageLstn) lstn(srcRef, updates);
// 	} else {
// 		//failed.
// 		//TODO notif('');
// 		console.log("Refresh failed.");
// 		Promise.reject("Refresh failed: " + resp.status);
// 	}
// }
